/**
 * Card preview registry: one static preview per frame type, drawn from
 * `previewSource(frame)` (the server card, or the full content while it is
 * newer). Previews mount no live runtime (editor, iframe, terminal, globe,
 * player) and never read the frame surface; the container (wall card,
 * zoomed-out canvas) owns the surface and maps the `--card-*` tokens.
 * Types without an entry render `GenericCardPreview` (title + excerpt).
 */
import type { ComponentType } from "react";
import type { FrameType } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";
import { AgendaCardPreview } from "./agenda-frame/agenda-card-preview";
import { CalendarCardPreview } from "./calendar-frame/calendar-card-preview";
import { ChartCardPreview } from "./chart-frame/chart-card-preview";
import { CodeCardPreview } from "./code-frame/code-card-preview";
import { ConnectionCardPreview } from "./connection-frame/connection-card-preview";
import { ConversationCardPreview } from "./conversation-frame/conversation-card-preview";
import { DatasetCardPreview } from "./dataset-frame/dataset-card-preview";
import { DocCardPreview } from "./doc-frame/doc-card-preview";
import { EventCardPreview } from "./event-frame/event-card-preview";
import { FileCardPreview } from "./file-frame/file-card-preview";
import { FlightCardPreview } from "./flight-frame/flight-card-preview";
import { GithubCardPreview } from "./github-frame/github-card-preview";
import { HtmlCardPreview } from "./html-frame/html-card-preview";
import { ImageCardPreview } from "./image-frame/image-card-preview";
import { LinkPreviewCardPreview } from "./link-preview-frame/link-preview-card-preview";
import { MemoryCardPreview } from "./memory-frame/memory-card-preview";
import { MusicCardPreview } from "./music-frame/music-card-preview";
import { PreviewCardCardPreview } from "./preview-card-frame/preview-card-card-preview";
import { TaskboardCardPreview } from "./taskboard-frame/taskboard-card-preview";
import { TerminalCardPreview } from "./terminal-frame/terminal-card-preview";
import { TodoCardPreview } from "./todo-frame/todo-card-preview";
import { WeatherCardPreview } from "./weather-frame/weather-card-preview";

/**
 * `regular`: a wall card, heights capped per type. `compact`: fills its box
 * (stacked project covers, the zoomed-out canvas).
 */
export type CardPreviewDensity = "regular" | "compact";

export interface CardPreviewProps {
  frame: FrameData;
  /** `previewSource(frame)`: card or content, one schema. */
  source: Record<string, unknown>;
  density: CardPreviewDensity;
}

export const cardPreviews: Partial<
  Record<FrameType, ComponentType<CardPreviewProps>>
> = {
  doc: DocCardPreview,
  note: DocCardPreview,
  markdown: DocCardPreview,
  chart: ChartCardPreview,
  dataset: DatasetCardPreview,
  html: HtmlCardPreview,
  image: ImageCardPreview,
  todo: TodoCardPreview,
  github: GithubCardPreview,
  code: CodeCardPreview,
  terminal: TerminalCardPreview,
  link_preview: LinkPreviewCardPreview,
  preview_card: PreviewCardCardPreview,
  weather: WeatherCardPreview,
  event: EventCardPreview,
  flight: FlightCardPreview,
  music: MusicCardPreview,
  agenda: AgendaCardPreview,
  calendar: CalendarCardPreview,
  file: FileCardPreview,
  taskboard: TaskboardCardPreview,
  memory: MemoryCardPreview,
  conversation: ConversationCardPreview,
  connection: ConnectionCardPreview,
};
